<template>
<v-container fluid grid-list-lg>
  <v-layout row wrap>
    <v-flex>
      <v-card>
        <v-card-title primary-title class="primary white--text headline">
          Annotation Work Import
        </v-card-title>
        <v-card-text>
          <form>
            <v-layout row wrap>
              <v-flex xs12 md6>
                <v-text-field
                  v-model="item.name"
                  label="Annotation Work Name"
                  v-validate="'required|min:3|max:50'"
                  data-vv-name="name"
                  :error-messages="errors.collect('name')"
                />
              </v-flex>
              <v-flex xs12 md6>
                <v-select
                  v-model="item.annotationType"
                  label="Annotation Type"
                  :items="annotationTypeCollection"
                  item-text="name"
                  item-value="id"
                  v-validate="'required'"
                  data-vv-name="annotationType"
                  :error-messages="errors.collect('annotationType')"
                />
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12 md6>
                <v-select
                  v-model="item.annotator"
                  label="Annotator"
                  :items="annotators"
                  item-text="text"
                  item-value="value"
                  v-validate="'required'"
                  data-vv-name="annotator"
                  :error-messages="errors.collect('annotator')"
                />
              </v-flex>
              <v-flex xs12 md6>
                <v-select
                  v-model="item.language"
                  label="Language"
                  :items="languages"
                  item-text="text"
                  item-value="value"
                  v-validate="'required'"
                  data-vv-name="language"
                  :error-messages="errors.collect('language')"
                  :disabled="item.useExistingDataset"
                />
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12>
                <v-checkbox
                  v-model="item.useExistingDataset"
                  label="Import into an existing dataset"
                />
              </v-flex>
            </v-layout>

            <v-layout row wrap v-if="item.useExistingDataset">
              <v-flex xs12>
                <v-select
                  v-model="item.datasetId"
                  label="Dataset"
                  :items="datasets"
                  item-text="text"
                  item-value="value"
                  v-validate="'required'"
                  data-vv-name="datasetId"
                  :error-messages="errors.collect('datasetId')"
                />
              </v-flex>
            </v-layout>

            <v-layout row wrap v-else>
              <v-flex xs12>
                <v-text-field
                  v-model="item.datasetName"
                  label="Dataset Name (optional)"
                  v-validate="'min:3|max:50'"
                  data-vv-name="datasetName"
                  :error-messages="errors.collect('datasetName')"
                  hint="Defaults to the annotation work name when left empty."
                  persistent-hint
                />
              </v-flex>
            </v-layout>

            <template v-if="Number(item.annotationType) === 1">
              <v-layout row wrap>
                <v-flex xs12>
                  <v-checkbox
                    v-model="item.createTopicModel"
                    label="Create a new topic model from labels in the Excel file"
                  />
                </v-flex>
              </v-layout>

              <v-layout row wrap v-if="item.createTopicModel">
                <v-flex xs12>
                  <v-alert :value="true" type="info" outline class="mb-0">
                    <div class="subheading mb-2">Each label in your file becomes a topic name</div>
                    <div class="mb-3">
                      A label must have 3 to 80 characters. The import stops and lists the
                      labels that break these rules, so nothing is created by mistake.
                    </div>
                    <div class="mb-1"><strong>Forbidden characters</strong></div>
                    <div class="mb-3">
                      <v-chip
                        v-for="character in forbiddenCharacters"
                        :key="`forbidden-${character}`"
                        small
                        label
                        class="import-character-chip"
                      >{{ character }}</v-chip>
                    </div>
                    <div class="mb-1">
                      <strong>Allowed</strong> — letters (Turkish letters included), digits,
                      space and:
                    </div>
                    <div>
                      <v-chip
                        v-for="character in allowedCharacters"
                        :key="`allowed-${character}`"
                        small
                        label
                        class="import-character-chip"
                      >{{ character }}</v-chip>
                    </div>
                  </v-alert>
                </v-flex>
              </v-layout>

              <v-layout row wrap v-if="item.createTopicModel">
                <v-flex xs12>
                  <v-text-field
                    v-model="item.topicModelName"
                    label="Topic Model Name (optional)"
                    v-validate="'min:3|max:50'"
                    data-vv-name="topicModelName"
                    :error-messages="errors.collect('topicModelName')"
                    hint="Defaults to the annotation work name when left empty."
                    persistent-hint
                  />
                </v-flex>
              </v-layout>

              <v-layout row wrap v-else>
                <v-flex xs12 md8>
                  <v-select
                    v-model="item.topicModelId"
                    label="Topic Model"
                    :items="topicModels"
                    item-text="text"
                    item-value="value"
                    v-validate="'required'"
                    data-vv-name="topicModelId"
                    :error-messages="errors.collect('topicModelId')"
                  />
                </v-flex>
                <v-flex xs12 md4>
                  <v-checkbox
                    v-model="item.ignoreUnmatchedLabels"
                    label="Ignore unmatched labels"
                  />
                </v-flex>
              </v-layout>
            </template>

            <v-layout row wrap>
              <v-flex xs12>
                <v-checkbox
                  v-model="item.noPreprocess"
                  label="Skip preprocessing for new instances"
                />
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12>
                <vue2-dropzone
                  :key="`annotation-work-import-dropzone-${item.annotationType}`"
                  ref="myVueDropzone"
                  id="annotation-work-import-dropzone"
                  :options="dropzoneOptions"
                  :use-custom-dropzone-options="true"
                  v-on:vdropzone-success="dropzone_success"
                  v-on:vdropzone-error="dropzone_error"
                />
              </v-flex>
            </v-layout>
          </form>
        </v-card-text>
        <v-card-actions style="background: hsl(0, 0%, 97%)" class="pa-3">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" flat depressed @click.native="cancel" :disabled="saveLoading">Cancel</v-btn>
          <v-btn color="accent" class="white--text" depressed @click.native="validateBeforeSubmit(save)" :loading="saveLoading" :disabled="saveLoading">Import</v-btn>
        </v-card-actions>
      </v-card>
    </v-flex>
  </v-layout>

  <v-dialog v-model="errorDialog" max-width="800" scrollable>
    <v-card>
      <v-card-title class="headline error white--text">{{ errorTitle }}</v-card-title>
      <v-card-text>
        <p class="import-error-message mb-0">{{ errorMessage }}</p>
      </v-card-text>
      <v-card-actions style="background: hsl(0, 0%, 97%)" class="pa-3">
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" flat depressed @click.native="errorDialog = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="skippedDialog" max-width="800" persistent scrollable>
    <v-card>
      <v-card-title class="headline">Import completed with skipped rows</v-card-title>
      <v-card-text>
        <p>
          {{ importResult.annotation_count }} rows were imported and
          {{ importResult.skipped_rows }} rows were skipped.
          The row numbers below are the row numbers of your Excel file.
        </p>
        <p v-if="importResult.negative_rows > 0">
          {{ importResult.negative_rows }} rows without a label were imported as negative examples (NOTOPIC).
        </p>
        <v-data-table
          :headers="skippedHeaders"
          :items="skippedDetails"
          :rows-per-page-items="[10, 25, 50]"
          class="elevation-1"
        >
          <template slot="items" slot-scope="props">
            <td>{{ props.item.row }}</td>
            <td>{{ props.item.message }}</td>
            <td>{{ props.item.detail }}</td>
          </template>
        </v-data-table>
        <p v-if="importResult.skipped_details_truncated" class="mt-2 grey--text">
          Only the first {{ skippedDetails.length }} skipped rows are listed.
          See the backend log for the rest.
        </p>
      </v-card-text>
      <v-card-actions style="background: hsl(0, 0%, 97%)" class="pa-3">
        <v-spacer></v-spacer>
        <v-btn color="accent" class="white--text" depressed @click.native="closeSkippedDialog">
          Continue
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</v-container>
</template>

<script>
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'annotation-work-import',
  components: {
    vue2Dropzone
  },
  created () {
    this.fetchFormData()
  },
  data () {
    return {
      item: {
        name: '',
        annotationType: 0,
        annotator: '',
        language: 'tr',
        useExistingDataset: false,
        datasetId: '',
        datasetName: '',
        createTopicModel: false,
        topicModelId: '',
        topicModelName: '',
        ignoreUnmatchedLabels: false,
        noPreprocess: false
      },
      annotators: [],
      languages: [],
      datasets: [],
      topicModels: [],
      uploadedFilename: null,
      fileSize: 0,
      saveLoading: false,
      skippedDialog: false,
      skippedDetails: [],
      importResult: {},
      errorDialog: false,
      errorTitle: '',
      errorMessage: '',
      skippedHeaders: [
        {text: 'Excel Row', sortable: true, value: 'row'},
        {text: 'Reason', sortable: true, value: 'message'},
        {text: 'Detail', sortable: false, value: 'detail'}
      ],
      // Kept in step with FORBIDDEN_NAME_CHARACTERS in api/helpers/utils.py.
      forbiddenCharacters: [
        '!', '#', '$', '%', '&', '(', ')', '*', '-', ':', '<', '>',
        '?', '@', '[', '\\', ']', '^', '_', '{', '|', '}', '~'
      ],
      allowedCharacters: ['.', ',', ';', "'", '"', '/', '+', '=']
    }
  },
  computed: {
    ...mapState({
      annotationTypeCollection: state => state.configurations.annotationTypes
    }),
    dropzoneOptions () {
      const annotationType = Number(this.item.annotationType)
      return {
        url: new URL(
          `annotation-works/import/upload?annotation_type=${annotationType}`,
          this.axios.defaults.baseURL
        ).href,
        thumbnailWidth: 150,
        acceptedFiles: '.xlsx,.xls',
        maxFilesize: 100,
        maxFiles: 1,
        headers: { 'Authorization': `${this.$auth.token()}` },
        addRemoveLinks: false
      }
    }
  },
  methods: {
    fetchFormData () {
      this.setLoader({
        loading: true
      })

      Promise.all([
        this.axios.get('/annotators'),
        this.axios.get('/languages'),
        this.axios.get('/datasets')
      ])
        .then(([annotatorsRes, languagesRes, datasetsRes]) => {
          this.annotators = annotatorsRes.data
            .filter(a => a.is_active)
            .map(a => ({
              value: a.id,
              text: `${a.first_name} ${a.last_name} (${a.email})`
            }))
          this.languages = languagesRes.data.map(language => ({
            value: language.code,
            text: language.name
          }))
          this.datasets = datasetsRes.data.map(dataset => ({
            value: dataset.id,
            text: dataset.name,
            language: dataset.language ? dataset.language.code : null
          }))
        })
        .catch(error => {
          this.handleAxiosError(error)
        })
        .finally(() => {
          this.setLoader({
            loading: false
          })
        })
    },
    loadTopicModels () {
      if (this.topicModels.length > 0) {
        return
      }
      this.axios.get('/topic-models')
        .then(response => {
          this.topicModels = response.data.map(topicModel => ({
            value: topicModel.id,
            text: topicModel.name
          }))
        })
        .catch(error => {
          this.handleAxiosError(error)
        })
    },
    cancel () {
      this.$router.push({ name: 'annotationWorkList' })
    },
    goToList () {
      this.$router.push({
        name: 'annotationWorkList',
        query: { imported: this.importResult.annotation_work_id }
      })
    },
    closeSkippedDialog () {
      this.skippedDialog = false
      this.goToList()
    },
    resetUploadedFile () {
      if (this.$refs.myVueDropzone) {
        this.$refs.myVueDropzone.removeAllFiles()
      }
      this.uploadedFilename = null
      this.fileSize = 0
    },
    dropzone_success (file, response) {
      this.uploadedFilename = response.filename
      this.fileSize = file.size / 1000
      this.setSnack({
        message: 'Your file has been uploaded successfully! Click import to process it.',
        type: 'success',
        timeout: 6000
      })
    },
    showError (title, message) {
      // These messages list the rows or labels that must be fixed, so they
      // stay on screen until the user closes them.
      this.errorTitle = title
      this.errorMessage = message
      this.errorDialog = true
    },
    dropzone_error (file, message, xhr) {
      let errorMessage = null
      this.resetUploadedFile()
      if (message && message.error_description != null) {
        errorMessage = message.error_description
      } else {
        errorMessage = message
      }
      this.showError('The file was not accepted', errorMessage)
    },
    save () {
      if (this.uploadedFilename == null) {
        this.setSnack({
          message: 'Please upload the annotation file.',
          type: 'error'
        })
        return
      }

      this.saveLoading = true

      const itemToSave = {
        filename: this.uploadedFilename,
        name: this.item.name,
        annotation_type: Number(this.item.annotationType),
        annotator_id: this.item.annotator,
        language: this.item.language,
        dataset_id: this.item.useExistingDataset ? this.item.datasetId : null,
        dataset_name: this.item.useExistingDataset ? null : (this.item.datasetName || null),
        create_topic_model: Number(this.item.annotationType) === 1 ? this.item.createTopicModel : false,
        topic_model_id: Number(this.item.annotationType) === 1 && !this.item.createTopicModel ? this.item.topicModelId : null,
        topic_model_name: Number(this.item.annotationType) === 1 && this.item.createTopicModel ? (this.item.topicModelName || null) : null,
        ignore_unmatched_labels: Number(this.item.annotationType) === 1 && !this.item.createTopicModel ? this.item.ignoreUnmatchedLabels : false,
        no_preprocess: this.item.noPreprocess
      }

      this.axios.post('/annotation-works/import', itemToSave)
        .then(response => {
          this.importResult = response.data
          this.skippedDetails = response.data.skipped_details || []
          const skippedRows = response.data.skipped_rows || 0
          const negativeRows = response.data.negative_rows || 0

          if (this.skippedDetails.length > 0) {
            // Show which rows were left out before leaving the page.
            this.skippedDialog = true
            return
          }

          const negativeNote = negativeRows > 0
            ? ` ${negativeRows} rows without a label were imported as negative examples (NOTOPIC).`
            : ''
          const message = (skippedRows > 0
            ? `Import completed with ${skippedRows} skipped rows.`
            : `Import completed. Created annotation work ${response.data.annotation_work_id}.`) + negativeNote
          this.setSnack({
            message,
            type: 'success',
            timeout: 6000
          })
          this.goToList()
        })
        .catch(error => {
          const response = error.response
          const description = response && response.data
            ? (response.data.error_description || response.data.error)
            : null
          // 400 means the file or the form is not valid. Those messages name
          // every label or row to fix, so show them in the dialog. Everything
          // else (session timeout, server error) keeps the shared handler.
          if (response && response.status === 400 && description) {
            this.showError('Import failed', description)
            return
          }
          this.handleAxiosError(error)
        })
        .finally(() => {
          this.resetUploadedFile()
          this.saveLoading = false
        })
    },
    ...mapActions({
      setSnack: 'snackbar/setSnack',
      setLoader: 'loader/setLoader'
    })
  },
  watch: {
    'item.annotationType' (annotationType) {
      const selectedType = Number(annotationType)
      this.item.annotationType = selectedType
      this.item.topicModelId = ''
      this.item.topicModelName = ''
      this.item.createTopicModel = false
      this.item.ignoreUnmatchedLabels = false
      this.resetUploadedFile()
      if (selectedType === 1) {
        this.loadTopicModels()
      }
    },
    'item.useExistingDataset' (useExistingDataset) {
      if (useExistingDataset) {
        this.item.datasetName = ''
        const selectedDataset = this.datasets.find(dataset => dataset.value === this.item.datasetId)
        if (selectedDataset && selectedDataset.language) {
          this.item.language = selectedDataset.language
        }
      } else {
        this.item.datasetId = ''
      }
    },
    'item.datasetId' (datasetId) {
      const selectedDataset = this.datasets.find(dataset => dataset.value === datasetId)
      if (selectedDataset && selectedDataset.language) {
        this.item.language = selectedDataset.language
      }
    },
    saveLoading (val) {
      this.setLoader({
        loading: val
      })
    }
  }
}
</script>

<style>
.import-character-chip {
  min-width: 34px;
}

.import-error-message {
  white-space: pre-wrap;
  word-break: break-word;
}

.import-character-chip .v-chip__content {
  font-family: monospace;
  font-size: 14px;
  font-weight: bold;
  justify-content: center;
  width: 100%;
}
</style>
